import { useMutation } from "@tanstack/react-query"
import { useQueryClient } from "@tanstack/react-query"

import apiClient from "@api/client"
import { QUERY_KEYS } from "@api/constants"
import { useCart } from "providers/cart-provider"

import { useActiveStore } from "./useActiveStore"
import { useDefaultDeliveryAddress } from "./useDefaultDeliveryAddress"

interface SetDefaultDeliveryAddressParams {
  onSuccess?: () => void
}

export const useSetDefaultDeliveryAddress = ({
  onSuccess,
}: SetDefaultDeliveryAddressParams) => {
  const { cart } = useCart()
  const { activeStore } = useActiveStore()
  const defaultDeliveryAddress = useDefaultDeliveryAddress()
  const queryClient = useQueryClient()

  const setDefaultDeliveryAddress = useMutation({
    mutationKey: ["SET_DEFAULT_DELIVERY_ADDRESS"],
    mutationFn: async (deliveryAddressId: number) => {
      const lastDeliveryAddress = defaultDeliveryAddress.data?.id

      await apiClient.post("user/delivery-address/default", {
        delivery_address_id: deliveryAddressId,
      })

      try {
        const res = await apiClient.put(
          `stores/${activeStore?.id}/cart/edit-delivery-type/`,
          {
            fulfillmentType: "DELIVERY",
            curbside_pickup_vehicle_make_model: "",
            curbside_pickup_vehicle_color: "",
            curbside_pickup_vehicle_notes: "",
            is_curbside: false,
          },
        )

        if (res?.statusCode != 200) {
          throw new Error(
            (res?.body as { message?: string })?.message ??
              "This address is not valid for delivery from this location.",
          )
        }
      } catch (e) {
        await apiClient.post("user/delivery-address/default", {
          delivery_address_id: lastDeliveryAddress,
        })

        // We aren't awaiting this to speed up the error response for the user.
        apiClient.put(`stores/${activeStore?.id}/cart/edit-delivery-type/`, {
          fulfillmentType: cart.data?.fulfillmentType,
          curbside_pickup_vehicle_make_model:
            cart.data?.curbsidePickupVehicleMakeModel,
          curbside_pickup_vehicle_color: cart.data?.curbsidePickupVehicleColor,
          curbside_pickup_vehicle_notes: cart.data?.curbsidePickupNotes,
          is_curbside: cart.data?.isCurbside,
        })

        throw e
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_CART] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SAVED_ADDRESSES] })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_DEFAULT_DELIVERY_ADDRESS],
      })

      onSuccess?.()
    },
  })

  return setDefaultDeliveryAddress
}
